/**
 * Tier Card — a "do not disturb" plaque from a luxury suite. Italic tier
 * name, hairline corner brackets, tone-graded background, subtle royal glow
 * on the top tier.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, formatHoursDecimal } from '../types.ts';
import type { Tier } from '../types.ts';

const TIER_TONE: Record<Tier, { fg: string; accent: string; rim: string; glow: boolean }> = {
  peasant:    { fg: 'oklch(70% 0.04 270)', accent: 'oklch(40% 0.04 270)', rim: 'oklch(35% 0.03 270 / 0.45)', glow: false },
  commoner:   { fg: 'oklch(78% 0.05 80)',  accent: 'oklch(55% 0.07 80)',  rim: 'oklch(50% 0.05 80 / 0.45)',  glow: false },
  gentry:     { fg: 'oklch(82% 0.09 90)',  accent: 'oklch(64% 0.11 75)',  rim: 'oklch(58% 0.10 75 / 0.55)',  glow: false },
  aristocrat: { fg: 'oklch(86% 0.13 90)',  accent: 'oklch(72% 0.14 80)',  rim: 'oklch(68% 0.14 80 / 0.65)',  glow: true  },
  royal:      { fg: 'oklch(91% 0.14 88)',  accent: 'oklch(78% 0.18 84)',  rim: 'oklch(75% 0.18 85 / 0.85)',  glow: true  },
};

export class AramgahTierCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.tier, s.summary.avg_hours);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(tier: Tier, avgHours: number): void {
    const tone = TIER_TONE[tier];
    const meta = TIER_DISPLAY[tier];
    const glowStyle = tone.glow
      ? `box-shadow: 0 0 70px ${tone.accent}33, inset 0 0 60px ${tone.accent}1c;`
      : '';
    const nameShadow = tone.glow ? `0 0 24px ${tone.accent}99, 0 0 50px ${tone.accent}33` : 'none';

    this.#shadow.innerHTML = /* html */ `
<style>
  :host {
    display: block;
    background:
      linear-gradient(165deg, color-mix(in oklch, ${tone.accent} 22%, var(--paper-card)) 0%, var(--paper-card) 60%, color-mix(in oklch, ${tone.accent} 8%, var(--paper-card-up)) 100%);
    border: 1px solid ${tone.rim};
    border-radius: 12px;
    padding: 34px 30px 38px;
    position: relative;
    overflow: hidden;
    isolation: isolate;
    ${glowStyle}
    container-type: inline-size;
  }
  :host::before, :host::after {
    content: '';
    position: absolute;
    width: 30px;
    height: 30px;
    border: 1px solid ${tone.rim};
    pointer-events: none;
    z-index: 1;
  }
  :host::before { top: 12px; left: 12px; border-right: none; border-bottom: none; }
  :host::after  { bottom: 12px; right: 12px; border-left: none; border-top: none; }
  .corner-tr, .corner-bl {
    position: absolute;
    width: 30px;
    height: 30px;
    border: 1px solid ${tone.rim};
    pointer-events: none;
    z-index: 1;
  }
  .corner-tr { top: 12px; right: 12px; border-left: none; border-bottom: none; }
  .corner-bl { bottom: 12px; left: 12px; border-right: none; border-top: none; }
  .label {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: ${tone.fg};
    opacity: 0.78;
    position: relative;
    z-index: 2;
  }
  .seal {
    width: 48px;
    height: 1px;
    background: ${tone.fg};
    margin: 14px 0 10px;
    opacity: 0.55;
    position: relative;
    z-index: 2;
  }
  .name {
    font-family: var(--display);
    font-size: clamp(48px, 13cqw, 78px);
    font-weight: 600;
    font-style: italic;
    letter-spacing: -0.022em;
    color: ${tone.fg};
    line-height: 0.95;
    text-shadow: ${nameShadow};
    text-wrap: balance;
    position: relative;
    z-index: 2;
  }
  .tagline {
    font-family: var(--display);
    font-style: italic;
    font-size: 19px;
    color: var(--ink-dim);
    margin-top: 16px;
    font-weight: 400;
    text-wrap: pretty;
    position: relative;
    z-index: 2;
  }
  .avg-row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px dashed ${tone.rim};
    position: relative;
    z-index: 2;
  }
  .avg-num {
    font-family: var(--display);
    font-size: clamp(38px, 11cqw, 58px);
    font-weight: 600;
    color: var(--ink);
    letter-spacing: -0.022em;
    line-height: 1;
    font-feature-settings: 'tnum' 1, 'ss01' 1;
  }
  .avg-cap {
    font-family: var(--mono);
    font-size: 10px;
    color: var(--ink-faint);
    letter-spacing: 0.3em;
    text-transform: uppercase;
  }
  /* DND glyph in top-right — subtle hotel-suite touch */
  .plaque-glyph {
    position: absolute;
    top: 28px;
    right: 28px;
    font-family: var(--display);
    font-style: italic;
    font-size: 13px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${tone.fg};
    opacity: 0.45;
    z-index: 2;
  }
  /* Crescent phase indicator — fills more as the tier ascends */
  .phase {
    position: absolute;
    top: 24px;
    right: 64px;
    width: 38px;
    height: 38px;
    z-index: 2;
    filter: drop-shadow(0 0 14px ${tone.accent}66);
    opacity: 0.92;
  }
  /* Ascent line — the trajectory micro-copy that sells the next tier */
  .ascent {
    margin-top: 22px;
    padding-top: 16px;
    border-top: 1px solid ${tone.rim};
    font-family: var(--display);
    font-style: italic;
    font-size: 14px;
    line-height: 1.5;
    color: ${tone.fg};
    opacity: 0.78;
    text-wrap: pretty;
    position: relative;
    z-index: 2;
    letter-spacing: 0.005em;
  }
  .ascent::before {
    content: '↗';
    display: inline-block;
    font-family: var(--mono);
    font-size: 11px;
    margin-right: 8px;
    color: ${tone.accent};
    opacity: 0.85;
    transform: translateY(-1px);
  }
</style>
<div class="corner-tr"></div>
<div class="corner-bl"></div>
<svg class="phase" viewBox="0 0 40 40" aria-hidden="true">
  <defs>
    <radialGradient id="phaseFill-${tier}" cx="35%" cy="40%" r="60%">
      <stop offset="0%" stop-color="${tone.fg}" stop-opacity="1"/>
      <stop offset="100%" stop-color="${tone.accent}" stop-opacity="1"/>
    </radialGradient>
  </defs>
  <circle cx="20" cy="20" r="15" fill="url(#phaseFill-${tier})"/>
  <circle cx="${this.#shadowOffsetForTier(tier)}" cy="17" r="14" fill="var(--paper-card)"/>
</svg>
<div class="plaque-glyph">DND</div>
<div class="label">aapka rutba</div>
<div class="seal"></div>
<div class="name">${meta.name}</div>
<div class="tagline">"${meta.tagline}"</div>
<div class="avg-row">
  <span class="avg-num">${formatHoursDecimal(avgHours)}</span>
  <span class="avg-cap">/ night avg · 30 raat</span>
</div>
<div class="ascent">${meta.ascent}</div>
`;
  }

  /**
   * Maps tier → x-offset for the occluding circle. Higher tier = more revealed moon.
   * peasant (waning crescent, tiny sliver) → royal (almost full).
   */
  #shadowOffsetForTier(tier: Tier): number {
    const offsets: Record<Tier, number> = {
      peasant:    27,
      commoner:   30,
      gentry:     33,
      aristocrat: 37,
      royal:      44,
    };
    return offsets[tier];
  }
}

customElements.define('aramgah-tier-card', AramgahTierCard);
